/**
 * The resource types.
 * @enum {string}
 */
const iResourceTypes = {
    FILE: 'file',
    URL: 'url',
    NOTE: 'note',
}

/**
 * Represents a file modal uploader.
 * @param {Object} initialValues - The initial values for the file modal uploader.
 * @param {Array} initialValues.files - The initial files.
 * You can add as many properties as you need but must have the following ones:
 * - name: string
 * - fileName: string
 * - size: string
 * - type: string
 * - url: string
 * @param {Array} initialValues.urls - The initial URLs.
 * You can add as many properties as you need but must have the following properties:
 * - name: string
 * - path: string
 * @param {Array} initialValues.notes - The initial notes.
 * You can add as many properties as you need but must have the following properties:
 * - name: string
 * - content: string
 * @returns {Object} - The file modal uploader Alpine object.
 */
export default (modalName, initialValues = {
    files: [],
    urls : [],
    notes: [],
}) => ({
    files: undefined,
    urls : undefined,
    notes: undefined,

    /**
     * Initializes the file modal uploader with the provided initial values.
     */
    init(){
        this._setResources(initialValues);
    },

    //* Functions -----------------

    /**
     * Adds a resource (file) to the file modal uploader.
     * @param {Object} $e - The file to be added.
     */
    onAddResource($e, type){
        switch (type) {
            case iResourceTypes.FILE:
                const files = $e.target.files
                for (const file of files) {
                    this.files.push({
                        name    : ''/* `${file.name} (${formatBytes(file.size)})` */,
                        fileName: file.name,
                        type    : file.type,
                        size    : formatBytes(file.size),
                        url     : URL.createObjectURL(file),
                        file
                    });
                }
                break;

            case iResourceTypes.URL:
                this.urls.push({
                    name: '',
                    path: $e
                });
                break;

            case iResourceTypes.NOTE:
                this.notes.push({
                    name   : '',
                    content: $e
                });
                break;

            default:
                throw new Error(`The resource type "${type}" is not supported`);
        }

        this.$dispatch(modalName, { files: this.files, urls: this.urls, notes: this.notes });
    },

    /**
     * Removes a resource (file) from the file modal uploader.
     * @param {Object} $event - The file to be removed.
     */
    onRemoveResource($event, type) {
        switch (type) {
            case iResourceTypes.FILE:
                this.files = this.files.filter(f => f !== $event);
                break;

            case iResourceTypes.URL:
                this.urls = this.urls.filter(f => f !== $event);
                break;

            case iResourceTypes.NOTE:
                this.notes = this.notes.filter(f => f !== $event);
                break;

            default:
                throw new Error(`The resource type "${type}" is not supported`);
        }

        this.$dispatch(modalName, { files: this.files, urls: this.urls, notes: this.notes });
    },

    onChangeResourceName(){
        this.$dispatch(modalName, { files: this.files, urls: this.urls, notes: this.notes });
    },

    _setResources(resources){
        if (resources.files && Array.isArray(resources.files)) {
            this.files = resources.files.map(file => {
                if (!file.hasOwnProperty('name'))
                    throw new Error(`The "name" property is required\n${JSON.stringify(file)}`);
                if (!file.hasOwnProperty('fileName'))
                    throw new Error(`The "fileName" property is required\n${JSON.stringify(file)}`);
                if (!file.hasOwnProperty('size'))
                    throw new Error(`The "size" property is required\n${JSON.stringify(file)}`);
                if (!file.hasOwnProperty('type'))
                    throw new Error(`The "type" property is required\n${JSON.stringify(file)}`);
                if (!file.hasOwnProperty('url'))
                    throw new Error(`The "url" property is required\n${JSON.stringify(file)}`);

                return file;
            });
        }

        if (resources.urls && Array.isArray(resources.urls)) {
            this.urls = resources.urls.map(url => {
                if (!url.hasOwnProperty('path'))
                    throw new Error(`The "path" property is required\n${JSON.stringify(url)}`);
                if (!url.hasOwnProperty('name'))
                    throw new Error(`The "name" property is required\n${JSON.stringify(url)}`);

                return url;
            });
        }

        if (resources.notes && Array.isArray(resources.notes)) {
            this.notes = resources.notes.map(note => {
                if (!note.hasOwnProperty('name'))
                    throw new Error(`The "name" property is required\n${JSON.stringify(note)}`);
                if (!note.hasOwnProperty('content'))
                    throw new Error(`The "content" property is required\n${JSON.stringify(note)}`);

                return note;
            });
        }
    },
});

/**
 * Formats the given bytes into a human-readable format.
 * @param {number} bytes - The number of bytes.
 * @returns {string} - The formatted bytes.
 */
function formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
